import { SocialMediaLinks } from '@/constants/SocialMediaLinks';
import { userRoleTypes } from '@/utils/userRoleTypes';
import { getSignupRoute } from '@/constants/featureFlags';
import { getStudioHubBasePath, getStudioHubLabel, shouldShowStudioHub } from '@/utils/isStudioUser';
import { TRC_ROUTES } from '@/TRC/constants/trcRoutes';
import {
	Favorite,
	Home,
	LiveTv,
	Person,
	RequestQuote,
	SchoolOutlined,
	Subscriptions,
	SupportAgent,
	VideoLibrary,
	MenuBook
} from '@mui/icons-material';

export const topSidebarItems = [
	{
		key: 'home',
		label: 'HOME',
		icon: Home,
		path: '/',
		noDefaultStyle: true
	},
	{
		key: 'library',
		label: 'LIBRARY',
		icon: VideoLibrary,
		path: '/AllVideos',
		noDefaultStyle: true,
		subItems: [
			{
				key: 'lesson',
				label: 'Lessons',
				path: '/Lessons'
			},
			{
				key: 'exercises',
				label: 'Exercises',
				path: '/Exercises'
			},
			{
				key: 'across',
				label: 'Across The Floor',
				path: '/Acrossthefloor'
			},
			{
				key: 'all',
				label: 'View All',
				path: '/AllVideos'
			}
		]
	},
	{
		key: 'playlist',
		label: 'Playlists',
		icon: Subscriptions,
		path: '/all-playlist',
		pathResolver: userData => {
			if (!userData?.token) return '/featured-playlist';
			return '/all-playlist';
		},
		subItems: [
			{
				key: 'featured',
				label: 'Featured Playlists',
				path: '/featured-playlist'
			},
			{
				key: 'personal',
				label: 'Personal Playlists',
				path: '/personal-playlist',
				requiresAuth: true
			},
			{
				key: 'studio',
				label: 'Studio Playlists',
				path: '/studio-playlist',
				requiresAuth: true,
				requiredUserTypes: ['studio-owner', 'subuser']
			}
		]
	},
	{
		key: 'classes',
		label: 'CLASSES',
		icon: LiveTv,
		path: '/classes',
		pathResolver: userData => {
			if (!userData.token) return getSignupRoute();
			if ((!userData?.user?.userType || userData?.user?.userType === '') && userData?.user?.role !== userRoleTypes.ADMIN) return '/Plans';

			return '/classes';
		},
		isItemLocked: userData => {
			if (!userData.token) return true;
			else if (!userData.subscriptionId && !userData.user?.userType && !userData.user?.subscription && userData?.role !== userRoleTypes.ADMIN) return true;
			else if ((!userData?.user?.userType || userData?.user?.userType === '') && userData?.user?.role !== userRoleTypes.ADMIN) return true;
			return false;
		}
	},
	{
		key: 'favourites',
		label: 'SAVED',
		icon: Favorite,
		path: '/saved',
		pathResolver: userData => {
			if (!userData?.user) {
				return getSignupRoute();
			}
			return '/saved';
		}
	},
	// {
	// 	key: 'lessonplan',
	// 	label: 'STUDIO LESSON PLAN',
	// 	icon: MenuBook,
	// 	path: '/lesson-plan',
	// 	requiresAuth: true,
	// 	requiredUserTypes: ['studio-owner', 'subuser']
	// },
	{
		key: 'planpage',
		label: 'PLANS',
		icon: RequestQuote,
		// Use a function to dynamically resolve the path
		pathResolver: userData => {
			if (!userData?.user) {
				return '/plans';
			}
			if (userData?.user?.userType === 'studio-owner') {
				return '/Plans';
			}
			return '/Plans';
		},
		path: '/Plans',
		restrictForAdmin: true,
		restrictForPaidUser: true
	},
	{
		key: 'account',
		label: 'MY ACCOUNT',
		icon: Person,
		path: '/account',
		requiresAuth: true
	}
];

export const bottomSidebarItems = [
	{
		key: 'trc',
		label: 'Teacher Resource Center',
		labelResolver: getStudioHubLabel,
		icon: SchoolOutlined,
		pathResolver: userData => {
			if (!userData.subscriptionId && !userData.user?.subscriptionId && !userData.user?.subscription) return '/plans';
			return shouldShowStudioHub(userData) ? getStudioHubBasePath(userData) : TRC_ROUTES.TRC_HOME;
		},
		path: TRC_ROUTES.TRC_HOME,
		external: false
	},
	{
		key: 'help',
		label: 'Help & Support',
		icon: SupportAgent,
		link: SocialMediaLinks.helpAndSupport,
		external: true
	}
];

export const getItemLockStatus = (item, userData) => {
	return item.isItemLocked ? item.isItemLocked(userData) : false;
};

export const getItemPath = (item, userData) => {
	return item.pathResolver ? item.pathResolver(userData) : item.link ? item.link : item.path;
};

export const getItemLabel = (item, userData) => {
	return item.labelResolver ? item.labelResolver(userData) : item.label;
};

export const getAllTopSidebarPaths = ({ includeSubItems = false } = {}) => {
	// Extract paths from topSidebarItems (including subItems if includeSubItems is true).
	const topPaths = topSidebarItems.reduce((paths, item) => {
		if (item.path) {
			paths.push(item.path.toLowerCase());
		}
		if (includeSubItems && item.subItems && Array.isArray(item.subItems)) {
			item.subItems.forEach(subItem => {
				if (subItem.path) {
					paths.push(subItem.path.toLowerCase());
				}
			});
		}
		return paths;
	}, []);

	return topPaths;
};

export const getParentPathForSubItem = inputPath => {
	const lowerInput = inputPath.toLowerCase();

	for (const parentItem of topSidebarItems) {
		// If the item has sub-items, iterate over them.
		if (Array.isArray(parentItem.subItems)) {
			for (const subItem of parentItem.subItems) {
				if (subItem.path.toLowerCase() === lowerInput) {
					// Found a match: return the parent's path.
					return parentItem.path;
				}
			}
		}
	}

	// If no sub-item matched, return the original path.
	return inputPath;
};
